import { API_ERROR_STATUS_BY_TYPE, API_ERROR_TYPES, ApiErrorBodySchema, DEFAULT_MCP_TOOL_PERMISSION, mcpToolOfferedName } from "@openharness/protocol";
//#region src/errors.ts
/**
* The errors `@openharness/client` throws.
*
* - {@link ApiError} — the server answered with a non-2xx status and (usually) the protocol's
*   error envelope. It carries the HTTP status, the `error.type` string and the request id,
*   so a caller can branch on the status or show the message.
* - {@link AuthenticationError} — the 401 case of that, as its own type so a frontend can tell
*   "sign in again" from every other failure without inspecting numbers or strings. It is an
*   {@link ApiError} subclass; the event stream stops on it rather than reconnecting.
* - {@link ResponseValidationError} — the server answered 2xx with a body that does not match
*   the protocol. That is a server bug, not a request one, so it gets its own type instead of
*   being flattened into an {@link ApiError}.
*
* A failed `fetch` itself (no network, DNS, TLS, an abort) is *not* wrapped: the original
* error rejects the promise. Only an answer from the server becomes an {@link ApiError}.
*/
/** The HTTP statuses that are worth retrying: rate limiting and server-side failures. */
const RETRYABLE_STATUSES = /* @__PURE__ */ new Set([
	429,
	500,
	502,
	503,
	504,
	529
]);
/**
* The error type a status maps to, when the response did not carry an error envelope.
*
* The inverse of the protocol's `API_ERROR_STATUS_BY_TYPE`, so a proxy or a load balancer
* that answers with bare HTML still produces a typed error: a 401 is an
* `authentication_error` whether or not the body says so.
*
* Two types share a status (#245 added `mode_unavailable_error` beside
* `invalid_provider_credential` at 422), so this answers the **first** type
* `API_ERROR_STATUS_BY_TYPE` declares for it. That is only ever a fallback: a response that
* is the protocol's envelope carries its own `error.type`, which is what an `ApiError` is
* built from, so the real type is never guessed.
*
* @param status the HTTP status of the response
*/
function errorTypeForStatus(status) {
	return API_ERROR_TYPES.find((type) => API_ERROR_STATUS_BY_TYPE[type] === status) ?? "api_error";
}
/**
* A non-2xx answer from the server.
*
* @example
* ```ts
* try {
*   await client.sessions.get(sessionId)
* } catch (error) {
*   if (error instanceof ApiError && error.type === 'not_found_error') showMissing()
* }
* ```
*/
var ApiError = class extends Error {
	/** HTTP status of the response, e.g. `404`. */
	status;
	/** The protocol's error type, e.g. `not_found_error`. */
	type;
	/** The server's request id, when it reported one. */
	requestId;
	constructor(status, message, options = {}) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.type = options.type ?? errorTypeForStatus(status);
		this.requestId = options.requestId;
	}
	/**
	* Whether retrying the request has a chance of succeeding: `429` and the `5xx` family.
	*
	* The event stream uses this to decide between reconnecting and giving up, and a caller
	* that sends user events can use it the same way.
	*/
	get retryable() {
		return RETRYABLE_STATUSES.has(this.status);
	}
};
/**
* The server does not know who is calling: no session cookie, no bearer token, or one that is
* missing, malformed, expired or revoked (401 `authentication_error`).
*
* A subclass of {@link ApiError}, so existing `instanceof ApiError` handling keeps working; the
* distinct type is what lets a frontend send the user to sign-in, or the CLI to `oh login`,
* without matching on a status number. It is never retryable — the event stream stops on it
* instead of reconnecting forever.
*
* @example
* ```ts
* try {
*   await client.me()
* } catch (error) {
*   if (error instanceof AuthenticationError) redirectToSignIn()
* }
* ```
*/
var AuthenticationError = class extends ApiError {
	constructor(message, options = {}) {
		super(401, message, {
			type: "authentication_error",
			requestId: options.requestId
		});
		this.name = "AuthenticationError";
	}
};
/**
* A 2xx response whose body did not match the protocol.
*
* Thrown instead of returning a half-typed object: a caller that gets a value out of this
* client can rely on its shape. Unknown *fields* are stripped by the protocol's schemas, so
* this only fires for a missing field, a wrong type, or a body that is not JSON at all.
*/
var ResponseValidationError = class extends Error {
	/** HTTP status of the response that failed to parse. */
	status;
	/** What the schema reported, when it said anything specific. */
	details;
	constructor(status, message, details) {
		super(message);
		this.name = "ResponseValidationError";
		this.status = status;
		this.details = details;
	}
};
/**
* Build the error for a non-2xx response: an {@link AuthenticationError} for a 401,
* an {@link ApiError} for everything else.
*
* A body carrying the protocol's envelope wins: its `error.type` and `error.message` are what
* the server meant. Anything else — HTML from a proxy, an empty body, a body shaped
* differently — falls back to the type the status maps to. A 401 is an
* {@link AuthenticationError} either way, envelope or not, exactly as the protocol defines it.
*
* @param status the response status
* @param body the parsed body, when the response had one that was JSON
* @param options `statusText` and the `request-id` header, when the transport has them
*/
function apiErrorFromResponse(status, body, options = {}) {
	const parsed = ApiErrorBodySchema.safeParse(body);
	if (status === 401) return new AuthenticationError(parsed.success ? parsed.data.error.message : describeStatus(status, options.statusText), { requestId: (parsed.success ? parsed.data.request_id : void 0) ?? options.requestId });
	if (parsed.success) return new ApiError(status, parsed.data.error.message, {
		type: parsed.data.error.type,
		requestId: parsed.data.request_id ?? options.requestId
	});
	return new ApiError(status, describeStatus(status, options.statusText), { requestId: options.requestId });
}
/** A readable description of a status with no error envelope to quote. */
function describeStatus(status, statusText) {
	return `The request failed with HTTP status ${status}${statusText === void 0 || statusText === "" ? "" : ` ${statusText}`}.`;
}
//#endregion
//#region src/internal/async.ts
/**
* Small async primitives the client and the fake client both need.
*
* Everything here uses Web APIs only (`setTimeout`, `AbortSignal`), so it works in a browser
* and in Node.
*/
/**
* A promise that resolves after `ms` milliseconds, or as soon as `signal` aborts.
*
* Aborting does not reject: callers check `signal.aborted` themselves, which keeps the
* reconnect loop of the event stream free of try/catch noise.
*
* @param ms milliseconds to wait
* @param signal cancels the wait; when omitted, the wait always runs to the end
*/
function sleep(ms, signal) {
	return new Promise((resolve) => {
		if (signal?.aborted === true) {
			resolve();
			return;
		}
		const onAbort = () => {
			clearTimeout(timer);
			resolve();
		};
		const timer = setTimeout(() => {
			signal?.removeEventListener("abort", onAbort);
			resolve();
		}, ms);
		signal?.addEventListener("abort", onAbort, { once: true });
	});
}
/**
* An unbounded FIFO queue consumed from an async loop: `next()` resolves with the oldest
* item, waits for one, or returns `null` once the queue is closed and drained.
*
* {@link AsyncQueue.close} is what ends a consumer's `for await` loop, so closing it is how
* the fake client stops a stream — on unsubscribe, and when the caller aborts.
*/
var AsyncQueue = class {
	#items = [];
	#waiters = [];
	#closed = false;
	/** Append `value`; a waiting consumer is woken up. Ignored after {@link close}. */
	push(value) {
		if (this.#closed) return;
		const waiter = this.#waiters.shift();
		if (waiter === void 0) this.#items.push(value);
		else waiter(value);
	}
	/** Stop accepting items and end every pending and future {@link next} call with `null`. */
	close() {
		this.#closed = true;
		for (const waiter of this.#waiters.splice(0)) waiter(null);
	}
	/** Whether {@link close} has been called. */
	get closed() {
		return this.#closed;
	}
	/** The oldest item, or `null` when the queue is closed and empty. */
	async next() {
		const item = this.#items.shift();
		if (item !== void 0) return item;
		if (this.#closed) return null;
		return new Promise((resolve) => {
			this.#waiters.push(resolve);
		});
	}
};
//#endregion
//#region src/resources/auth.ts
/**
* Signing the CLI in: the device-code flow and session revocation, over Better Auth's
* `/api/auth/*` surface (epic #65, A1 and A6).
*
* This is not part of the `/v1` protocol — it is Better Auth's own API, which the server
* mounts under `/api/auth` — so the endpoints below are Better Auth's device-authorization
* plugin as documented, RFC 8628:
*
* ```
* POST /api/auth/device/code    start   -> the codes and where to approve them
* POST /api/auth/device/token   poll    -> the session token, or a polling error
* POST /api/auth/sign-out       revoke  -> ends the session the caller presented
* ```
*
* The flow from the CLI's point of view: {@link AuthResource.startDeviceLogin} gets the codes,
* the user approves them in a browser, and {@link AuthResource.pollDeviceLogin} polls until
* they do. The token that comes out is an opaque session token — the same kind of session a
* cookie holds (A2) — which the CLI stores and sends as `Authorization: Bearer`.
*/
/**
* The `client_id` `oh login` presents to the device flow.
*
* Better Auth hands it to the plugin's `validateClient`, and #61 registers exactly this value
* on the server; a server that does not know it answers `invalid_grant`.
*/
const OPENHARNESS_CLI_CLIENT_ID = "openharness-cli";
/** Where the server mounts Better Auth (`/api/auth/device/code`, `/api/auth/sign-out`, ...). */
const AUTH_PATH_PREFIX = "/api/auth";
/** The scopes the CLI asks for; the flow is session-based, so this is simply the documented set. */
const DEVICE_LOGIN_SCOPE = "openid profile email";
/** RFC 8628's default polling interval, in seconds, when the caller does not pass one. */
const DEFAULT_POLL_INTERVAL_SECONDS = 5;
/**
* The device login ended without a token, in a way only the user can resolve.
*
* `authorization_pending` and `slow_down` never surface here — they are the flow working as
* designed and the poll loop handles them. What does: the code `expired_token` (start again),
* `access_denied` (the user said no), and the failures the server answers with instead —
* `invalid_grant`, `invalid_client`, `invalid_request`, `server_error`.
*
* @example
* ```ts
* catch (error) {
*   if (error instanceof DeviceLoginError && error.code === 'expired_token') retryLogin()
* }
* ```
*/
var DeviceLoginError = class extends Error {
	/**
	* The RFC 8628 error code: `expired_token`, `access_denied`, `invalid_grant`,
	* `invalid_client`, `invalid_request` or `server_error`.
	*/
	code;
	/** The server's `error_description`, when it gave one. */
	description;
	constructor(code, description) {
		super(description === void 0 || description === "" ? `The device login failed (${code}).` : description);
		this.name = "DeviceLoginError";
		this.code = code;
		this.description = description;
	}
};
/** Build the auth resource over a transport. */
function createAuthResource(transport) {
	return {
		async startDeviceLogin(options) {
			const response = await transport.json(DEVICE_CODE_SCHEMA, {
				method: "POST",
				path: `${AUTH_PATH_PREFIX}/device/code`,
				body: {
					client_id: OPENHARNESS_CLI_CLIENT_ID,
					scope: DEVICE_LOGIN_SCOPE
				},
				signal: options?.signal
			});
			return {
				deviceCode: response.device_code,
				userCode: response.user_code,
				verificationUri: response.verification_uri,
				verificationUriComplete: response.verification_uri_complete,
				interval: response.interval,
				expiresIn: response.expires_in
			};
		},
		async pollDeviceLogin(deviceCode, options = {}) {
			let interval = options.interval ?? DEFAULT_POLL_INTERVAL_SECONDS;
			for (;;) {
				await sleep(interval * 1e3, options.signal);
				options.signal?.throwIfAborted();
				const response = await transport.rawJson({
					method: "POST",
					path: `${AUTH_PATH_PREFIX}/device/token`,
					body: {
						grant_type: "urn:ietf:params:oauth:grant-type:device_code",
						device_code: deviceCode,
						client_id: OPENHARNESS_CLI_CLIENT_ID
					},
					signal: options.signal
				});
				if (response.status >= 200 && response.status < 300) {
					const parsed = DEVICE_TOKEN_SCHEMA.safeParse(response.body);
					if (!parsed.success) throw new ResponseValidationError(response.status, "The server returned a device token response that does not match RFC 8628.", parsed.error.message);
					return parsed.data.access_token;
				}
				const failure = parseDeviceError(response.body);
				if (response.status === 400 && failure !== null) {
					if (failure.error === "authorization_pending") continue;
					if (failure.error === "slow_down") {
						interval += 5;
						continue;
					}
					throw new DeviceLoginError(failure.error, failure.error_description);
				}
				throw apiErrorFromResponse(response.status, response.body, {
					statusText: response.statusText,
					requestId: response.requestId
				});
			}
		},
		signOut(options) {
			return transport.noContent({
				method: "POST",
				path: `${AUTH_PATH_PREFIX}/sign-out`,
				body: {},
				signal: options?.signal
			});
		}
	};
}
const DEVICE_CODE_SCHEMA = { safeParse(value) {
	const record = asRecord(value);
	const deviceCode = record?.device_code;
	const userCode = record?.user_code;
	const verificationUri = record?.verification_uri;
	const verificationUriComplete = record?.verification_uri_complete;
	const interval = record?.interval;
	const expiresIn = record?.expires_in;
	if (typeof deviceCode !== "string" || typeof userCode !== "string" || typeof verificationUri !== "string" || verificationUriComplete !== void 0 && typeof verificationUriComplete !== "string" || typeof interval !== "number" || typeof expiresIn !== "number") return {
		success: false,
		error: { message: "The body is not a device-code response (RFC 8628 §3.2)." }
	};
	return {
		success: true,
		data: {
			device_code: deviceCode,
			user_code: userCode,
			verification_uri: verificationUri,
			verification_uri_complete: verificationUriComplete,
			interval,
			expires_in: expiresIn
		}
	};
} };
const DEVICE_TOKEN_SCHEMA = { safeParse(value) {
	const accessToken = asRecord(value)?.access_token;
	if (typeof accessToken !== "string" || accessToken === "") return {
		success: false,
		error: { message: "The body carries no access_token (RFC 8628 §3.5)." }
	};
	return {
		success: true,
		data: { access_token: accessToken }
	};
} };
/** The polling error the body describes, or `null` when it is not one. */
function parseDeviceError(value) {
	const record = asRecord(value);
	const error = record?.error;
	if (typeof error !== "string") return null;
	const description = record?.error_description;
	return {
		error,
		error_description: typeof description === "string" ? description : void 0
	};
}
/** The value as a plain object, or `undefined` for anything else (arrays included). */
function asRecord(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value) ? value : void 0;
}
//#endregion
//#region src/internal/events.ts
/**
* Small event helpers the client and the fake client both need.
*
* `send` takes one event or a list of them — the wire always carries the list — and both the
* real client and the fake have to tell the two call shapes apart without guessing from the
* contents of a single event. One implementation, because two would drift (#105).
*/
/** One event to append or a list of them, without guessing from the contents of one. */
function isEventList(events) {
	return Array.isArray(events);
}
//#endregion
//#region src/compaction.ts
/**
* How full the context is, and where the line the user chose sits (epic #277, K10/C1–C4; #280).
*
* The meter both frontends draw is the same arithmetic over the same two facts: the prompt size
* of the last real model request, and the **budget** of the model the chat currently runs. Both
* are things a transcript already holds (the size) or a catalog already answers (the budget), so
* this module is a handful of pure functions and nothing else — no store, no I/O.
*
* ## The budget comes from the server
*
* A model entry carries the **budget the brain will actually trim to** as `context_budget`
* (epic #277, C3/C5; #246, #280): the server resolves it with the same resolver it hands the
* brain, so a meter and the trimming that really happens cannot disagree. That matters most for
* a model the bundled registry knows nothing about — a custom OpenAI-compatible endpoint, an
* Azure deployment — where the catalog can still describe a window while the brain's resolver
* has nothing to derive a budget from and falls back to `DEFAULT_CONTEXT_TOKEN_BUDGET`. Deriving
* a budget from the window here instead would measure against a number no request is trimmed to.
*
* {@link contextTokenBudget} stays as the **fallback** for a response that predates the field
* (an older server, a caller that made its own entry): the server's rule restated —
*
* ```
* budget = contextWindow − min(maxOutput, 25% of contextWindow)
* ```
*
* — and `DEFAULT_CONTEXT_TOKEN_BUDGET` (32,768, the brain's own) for a model whose window even
* that cannot describe. It is restated rather than shared because the rule lives in a package
* this one may not depend on, and both frontends need it (the web header and the `oh` status
* line must agree). Keep the two in step: it is one rule written twice.
*
* ## What the meter measures
*
* The size is the **real prompt size** of the last real request — the three input-side counters
* of its `span.model_request_end.model_usage` summed, which is `promptTokensOf` in the brain. A
* request the compaction engine made (`purpose: 'summary'`) measures the summarizer's prompt, not
* the chat's, so the transcript refuses it as the meter's baseline — see {@link TranscriptContext}.
*
* After a summary lands, and until the next real request measures itself, the meter is an
* **estimate**: the baseline less what the summary replaced, plus the summary's own text. It is
* lower than the baseline (that is the point of the summary), it is flagged
* ({@link ContextMeter.estimated}), and it is usually seconds old — the request the compaction was
* making room for reports its real size immediately afterwards.
*
* ## The threshold
*
* Compaction fires at a share of the budget: the caller's stored `compaction_threshold`
* preference, or the deployment's own share (`defaults.compaction_threshold`) for one they never
* chose. {@link compactionThreshold} is the one place that lookup lives, so the frontends read
* the preference the same way and a default change is one edit.
*/
/** The share of a model's budget at which older history is summarized when nothing says otherwise. */
const DEFAULT_COMPACTION_THRESHOLD = .7;
/**
* The history budget a model with no known limits gets — the brain's own fallback
* (`DEFAULT_CONTEXT_TOKEN_BUDGET`), so a model the catalog does not carry is measured against the
* same number the server trims to.
*/
const DEFAULT_CONTEXT_TOKEN_BUDGET = 32768;
/** The share of a context window reserved for the reply when a model declares no output ceiling. */
const OUTPUT_RESERVE_RATIO = .25;
/** The characters-per-token estimate the budget is measured in, as the brain measures it. */
const CHARS_PER_TOKEN = 4;
/**
* The history budget one model's limits leave: `contextWindow − min(maxOutput, 25%)`.
*
* The rule the server's `contextTokenBudget` applies (#246), restated for the frontends.
*
* @param model the catalog's limits for the model; `contextWindow` is tokens, and `maxOutput` is
*   the model's own ceiling when the catalog has one
*/
function contextTokenBudget(model) {
	const share = Math.floor(model.contextWindow * OUTPUT_RESERVE_RATIO);
	const reserve = model.maxOutput === void 0 ? share : Math.min(model.maxOutput, share);
	return model.contextWindow - reserve;
}
/**
* The history budget of a catalog entry, in tokens.
*
* The server's `context_budget` is the answer whenever the entry carries one — it is the number
* the brain trims to, resolved by the server with the same resolver (`#246`, `#280`), which is
* what keeps this meter honest for a model the registry does not know. The window rule below is
* the fallback for an entry that predates the field, and `DEFAULT_CONTEXT_TOKEN_BUDGET` the
* fallback of *that* for an entry with no window either (C6's `null`) — the brain's own number,
* so a model nobody can size is measured against exactly what the server would trim it to.
*
* @param entry the catalog's entry for the model the meter is drawn against, if it has one
*/
function modelContextBudget(entry) {
	const reported = entry?.context_budget;
	if (typeof reported === "number" && reported > 0) return reported;
	const window = entry?.context_window;
	if (window === null || window === void 0 || window <= 0) return DEFAULT_CONTEXT_TOKEN_BUDGET;
	const maxOutput = entry?.max_output_tokens;
	return contextTokenBudget({
		contextWindow: window,
		...maxOutput === null || maxOutput === void 0 ? {} : { maxOutput }
	});
}
/**
* The share of the budget a chat compacts at: the caller's stored choice when they made one,
* else the default the server reports (epic #277; C3, #282).
*
* **The one place the preference is read.** `compaction_threshold` is the caller's own share or
* `null` for "follow the deployment's", and the deployment's number is not knowable here — a
* server may set `OPENHARNESS_COMPACTION_THRESHOLD` to anything — so a `null` is answered with
* the `defaults.compaction_threshold` the preferences response carries, which is the same value
* the server resolves for that owner's chats. `DEFAULT_COMPACTION_THRESHOLD` is what is left for
* a caller that has no preferences at all (a failed read, or a server that predates the route):
* the deployment's own share is the only fact this package cannot derive, and everything else is
* the server's answer.
*
* @param preferences the caller's stored preferences, as `GET /v1/me/preferences` answers them
*/
function compactionThreshold(preferences) {
	return preferences?.compaction_threshold ?? preferences?.defaults.compaction_threshold ?? .7;
}
/** A string's token cost, at the same characters-per-token estimate the budget is measured in. */
function estimateTokens(text) {
	return Math.ceil(text.length / 4);
}
/**
* The context size right after a summary replaced the history it covers (epic #277, K10; #280).
*
* The baseline — the last real request's measured prompt size, or the summary's own
* `tokens_before` when there has been none — counted the history the summary just replaced. What
* is left is that baseline, less the covered conversation (estimated from the text the transcript
* still holds, at {@link estimateTokens}), plus the summary's own text. Floored at the summary
* itself, which the model is at least told.
*
* An **estimate**, deliberately: nothing measures a prompt until a request is made with it, and
* the request the summary was made for reports its real size immediately afterwards. It
* over-counts rather than under-counts (the covered text estimate leaves out framing the real
* prompt paid for), which is the safe direction for a meter whose job is to warn early.
*
* @param input.baseline the request's measured prompt size, in tokens
* @param input.summary the summary's text
* @param input.coveredText the text of the conversation the summary covers
*/
function contextAfterSummary(input) {
	const summaryTokens = estimateTokens(input.summary);
	const replaced = estimateTokens(input.coveredText) - summaryTokens;
	return Math.max(summaryTokens, Math.round(input.baseline - replaced));
}
/**
* The meter for a transcript's context, or `null` when nothing has measured one yet.
*
* `null` is a real answer, not a failure: a chat that has not made a request has no prompt size
* to draw, and a caller with no catalog still gets a meter — against the default budget, since
* the model's window is the one thing it could not look up. The threshold is the caller's, from
* {@link compactionThreshold}.
*
* @param context what the transcript knows about the last real request, or `null`
* @param options.model the catalog's entry for the model the chat currently runs
* @param options.threshold the share of the budget that counts as near-full; the default applies
*/
function contextMeter(context, options = {}) {
	if (context === null) return null;
	const budget = modelContextBudget(options.model);
	const threshold = options.threshold ?? .7;
	const ratio = budget <= 0 ? 0 : context.tokens / budget;
	const percent = Math.round(ratio * 100);
	const marker = context.estimated ? "~" : "";
	return {
		tokens: context.tokens,
		budget,
		threshold,
		ratio,
		percent,
		nearThreshold: ratio >= threshold,
		estimated: context.estimated,
		label: `${marker}${String(percent)}% of context used`,
		shortLabel: `${marker}${String(percent)}%`
	};
}
/**
* The divider's own words for a summary (epic #277, K10; #280): why it happened and what wrote it,
* in the order a reader asks — reason, model, passes.
*
* Keyed by the reason rather than spelled out at each renderer, so the transcript's divider and
* the `oh` transcript say the same thing. `automatic` is the protocol's `threshold`: the context
* reached the share the settings allow, which is the one reason nobody asked for.
*
* @param summary the divider's summary, from `selectSummaries`
*/
function summaryDescription(summary) {
	const reason = summary.reason === "threshold" ? "automatic" : summary.reason === "overflow" ? "overflow" : "manual";
	const passes = summary.passes === 1 ? "1 pass" : `${String(summary.passes)} passes`;
	return `${reason} · ${summary.model} · ${passes}`;
}
/**
* How much of the summary model's budget one pass may spend on history (K5's
* `SUMMARY_SLICE_RATIO`): half. The other half is room for the answer, so a pass folds at most
* `summaryBudget / 2` tokens of history.
*/
const SUMMARY_SLICE_RATIO = .5;
/**
* Whether the chosen summary model will need more passes than the limit allows, so the chat
* model summarizes instead (K5) — `null` when it will not.
*
* A pass folds at most half the summary model's budget (the other half is its own answer), so
* `maxPasses` passes fold at most `maxPasses × summaryBudget / 2` tokens. When that is less
* than the chat model's own budget, any history the chat could fill needs more passes than the
* reader allows and the engine hands the work back to the chat model — which is the warning.
*
* The engine's other caps only ever make a slice *smaller*, so ignoring them here can only
* under-warn: a model this says is fine may still fall back over a very long chat, but one it
* flags will.
*
* @param options the chat model the reader's default names, the chosen summary model, and the
*   pass limit in force (the stored one, or the `defaults` the response reports)
*/
function summaryModelFallback(options) {
	const chatBudget = modelContextBudget(options.chat);
	const summaryBudget = modelContextBudget(options.summary);
	const slice = Math.floor(summaryBudget * SUMMARY_SLICE_RATIO);
	const passesNeeded = slice <= 0 ? Number.POSITIVE_INFINITY : Math.ceil(chatBudget / slice);
	return passesNeeded > options.maxPasses ? {
		chatBudget,
		summaryBudget,
		passesNeeded,
		maxPasses: options.maxPasses
	} : null;
}
/**
* What a manual compaction says while the brain has not answered it yet (epic #277, K8; #283):
* `Compacting…`.
*
* A `/compact` is stored and answered asynchronously, so a UI that said nothing between the
* ask and the outcome would look like it had ignored the reader. Both frontends draw this word,
* from {@link TranscriptManualCompaction.pending} — one string, so the terminal and the web
* cannot word the same wait differently.
*/
const COMPACTING_LABEL = "Compacting…";
/**
* The notice a manual compaction's outcome is owed, or `null` when no notice is needed
* (epic #277, K8; #283).
*
* `summarized` is `null`: the summary landed, the conversation shows C5's divider for it, and a
* second line saying so would be noise. The other two outcomes are exactly what the epic asks a
* client to show — "there was no older history to summarize" and "the summary could not be
* written" — and the brain's `message` is preferred over these fallbacks wherever it sent one,
* because it knows why. The words live here rather than in each frontend so `oh` and the web
* say the same thing, the same reason `summaryDescription` does.
*
* @param compaction the state `selectManualCompaction` answers
*/
function manualCompactionNotice(compaction) {
	if (compaction.outcome === null || compaction.outcome === "summarized") return null;
	if (compaction.outcome === "failed") return {
		tone: "error",
		text: compaction.message ?? "The summary could not be written."
	};
	return {
		tone: "info",
		text: compaction.message ?? "There was no older history to summarize."
	};
}
/**
* The line a reader is owed when a request had to shorten the newest **message** (epic #277, K6;
* #280; #314).
*
* The transcript hands this only a message's cut: a tool result's cut is its own notice
* (`truncatedResultsNotice`), and reading the request's truncation record as this one is what
* made a shortened `web_fetch` answer report the reader's own message as too long (#314 — the
* record names the newest item the request shortened, whichever kind it was, and
* `messageTruncation` in `./transcript` is the rule that tells the two apart).
*
* The words live here rather than in each frontend, like the manual-compaction notice's, so the
* web and `oh` cannot say different things about the same log.
*/
function truncationNotice(truncation) {
	const omitted = Math.max(0, truncation.tokensBefore - truncation.tokensAfter);
	return {
		text: "Your message was too long for this model and was shortened.",
		detail: `About ${String(omitted)} tokens of it were left out of the request.`
	};
}
//#endregion
//#region src/mcp.ts
/**
* The remote-MCP view both frontends share (epic #303, X10; issue #313).
*
* A server settings screen and a call line have to say the same things — what a server's status
* is, how its authentication is spelled, what its tool definitions cost, which tools belong to
* it and under what permission — and so does the notice a failed turn is owed. Those words and
* derivations live here once, so the page and the terminal cannot disagree, and so a component
* does not have to know the wire's field names.
*
* The wire half is {@link
* import('./resources/mcp-servers').McpServersResource} (`/v1/me/mcp_servers`) and
* `client.tools` (`/v1/me/tools`, whose `mcp` map holds a remote tool's permission). The tool
* summaries a server's last check listed are on the resource itself; a tool's **policy** is the
* settings' answer, and {@link mcpServerTools} joins the two.
*/
/** How a server authenticates, in the order a form offers them (epic #303, X10). */
const MCP_AUTH_TYPES = [
	"none",
	"headers",
	"oauth"
];
/** The words for an auth type, the same in both frontends. */
const AUTH_LABELS = {
	none: "None",
	headers: "Headers",
	oauth: "OAuth"
};
/**
* What an auth type means, as the one-line hint a form shows beside the choice.
*
* `headers` and `oauth` are the two secret-carrying kinds, and the hint says which secret the
* reader will be asked for rather than naming the field, because that is the decision.
*/
const AUTH_HINTS = {
	none: "The server needs no credentials — it is open, or it trusts the network.",
	headers: "Send a header on every request, e.g. an API key the server issued.",
	oauth: "Sign in to the server with OAuth. You will be sent to its authorization page."
};
/**
* The words for an auth type.
*
* @param auth the server's authentication
*/
function mcpAuthLabel(auth) {
	return AUTH_LABELS[auth];
}
/**
* The sentence a form shows for an auth type.
*
* @param auth the server's authentication
*/
function mcpAuthHint(auth) {
	return AUTH_HINTS[auth];
}
/** The words for a server's status (epic #303, X10). */
const STATUS_LABELS = {
	connected: "Connected",
	needs_reconnect: "Needs reconnect",
	error: "Error"
};
const STATUS_TONES = {
	connected: "ok",
	needs_reconnect: "warning",
	error: "error"
};
/**
* The words for a server's status.
*
* @param status the server's `status`
*/
function mcpStatusLabel(status) {
	return STATUS_LABELS[status];
}
/**
* The tone a server's status is drawn in.
*
* @param status the server's `status`
*/
function mcpStatusTone(status) {
	return STATUS_TONES[status];
}
/**
* The share of a server's tools that are usable right now, as the sentence under its status.
*
* The status is the headline; this is the detail a row shows beneath it — why a server is in
* `error`, when it was last checked, or that it has never been checked. A server in
* `needs_reconnect` with a `last_error` shows that error (a failed refresh is the reason the
* flow is being offered again); a `connected` one shows what its tools cost.
*
* @param server the server resource
*/
function mcpStatusDetail(server) {
	if (server.status === "error" || server.last_error !== null) return server.last_error ?? "The last check failed.";
	if (server.status === "needs_reconnect") return "Sign in to the server to use its tools.";
	if (server.last_tested_at === null) return "Not checked yet.";
	if (server.tools.length === 0) return "No tools listed.";
	return `${server.tools.length === 1 ? "1 tool" : `${server.tools.length} tools`}, about ${estimatedTokensLabel(server.definition_tokens)}.`;
}
/**
* An estimated token count, written as the estimate it is (epic #303, X6).
*
* The number is the protocol's cheap four-characters-per-token rule, not a tokenizer — it is
* what tells a reader one server costs about 2k tokens of a request's context and another about
* 200 — so it is always prefixed with `~` and never presented as a measurement. Above a
* thousand it is abbreviated (`~2.1k tokens`), which is the precision the estimate has anyway.
*
* @param tokens the estimate, in tokens
*/
function estimatedTokensLabel(tokens) {
	if (tokens < 1e3) return `~${tokens} tokens`;
	const thousands = tokens / 1e3;
	return `~${thousands < 10 ? Math.round(thousands * 10) / 10 : Math.round(thousands)}k tokens`;
}
/**
* What to say after a **Test** (issue #314).
*
* Testing a server that cannot answer used to look like nothing happened: the row's status went
* back to the same value and no line was drawn, so a reader pressed the button again. This is
* the one line both frontends draw instead, and it is deliberately explicit about the two cases
* that are not failures of the server:
*
* - an **`oauth` server that is not connected** never ran a check at all — the resolve finds no
*   token and answers `null` — so the result is "Connect first", not an empty success. A caller
*   should not offer Test until then, but a race (a token dropped in another tab) can still
*   reach this, and a reader deserves the real reason;
* - a **connected** server reports what its last check listed, in the same estimate the row's
*   detail line uses, so the result confirms something rather than restating the status.
*
* Every other outcome carries the server's own `last_error`, which now names the HTTP status
* and quotes the body (the server's report, not this module's).
*
* @param server the refreshed server a `test` answered with
*/
function mcpTestOutcome(server) {
	if (server.status === "connected") {
		if (server.tools.length === 0) return {
			ok: true,
			message: "Connected. The server lists no tools."
		};
		return {
			ok: true,
			message: `Connected. Listed ${server.tools.length === 1 ? "1 tool" : `${server.tools.length} tools`}, about ${estimatedTokensLabel(server.definition_tokens)}.`
		};
	}
	if (server.last_error !== null) return {
		ok: false,
		message: server.last_error
	};
	if (server.auth === "oauth") return {
		ok: false,
		message: "Connect first — this server needs a sign-in."
	};
	return {
		ok: false,
		message: "The check failed."
	};
}
/**
* A server's tools, each with the permission it runs under (epic #303, X6; #313).
*
* The two halves come from two reads, because they are two facts: the connection check listed
* the tools and priced their definitions (`McpServer.tools`), and the reader's permission for
* each is a **tool setting** (`GET /v1/me/tools`, a remote entry under the offered name). A tool
* the check listed and the settings do not name reads its own declaration — every remote tool
* declares `ask` (`DEFAULT_MCP_TOOL_PERMISSION`) — and a stored policy for a tool nothing
* lists is joined in beside them rather than dropped.
*
* @param server the server resource, with the tools its last check listed
* @param entries the tool settings, as `client.tools.list()` answers them
*/
function mcpServerTools(server, entries) {
	const policies = /* @__PURE__ */ new Map();
	const stored = [];
	for (const entry of entries) {
		if (entry.source !== "mcp" || entry.mcp_server !== server.name) continue;
		policies.set(entry.name, entry.policy);
		stored.push(entry.name);
	}
	const tools = server.tools.map((tool) => {
		const offeredName = mcpToolOfferedName(server.name, tool.name);
		return {
			offeredName,
			name: tool.name,
			description: tool.description,
			definitionTokens: tool.definition_tokens,
			policy: policies.get(offeredName) ?? DEFAULT_MCP_TOOL_PERMISSION,
			listed: true
		};
	});
	const listed = new Set(tools.map((tool) => tool.offeredName));
	for (const offeredName of stored) {
		if (listed.has(offeredName)) continue;
		tools.push({
			offeredName,
			name: offeredName.slice(server.name.length + 2),
			description: null,
			definitionTokens: null,
			policy: policies.get(offeredName) ?? DEFAULT_MCP_TOOL_PERMISSION,
			listed: false
		});
	}
	return tools;
}
/**
* The body that writes one remote tool's permission, and nothing else (epic #303, #312).
*
* A remote tool has no on/off of its own — a server is on or off as a resource — so `PUT
* /v1/me/tools` is asked for a **policy only**, through the `mcp` map. Building it here keeps a
* screen from writing a `builtin` half it did not mean to touch, which the merge would apply.
*
* @param offeredName the tool's model-facing name
* @param policy the permission to store
*/
function mcpToolPolicyBody(offeredName, policy) {
	return { mcp: { [offeredName]: policy } };
}
/**
* The body that writes one permission for **every** tool of a server (epic #303, #313).
*
* The batch a server's own control sends: one `PUT /v1/me/tools` naming every tool the server
* has — the ones its last check listed and a stored choice for a tool it no longer offers —
* each at the same permission, and the `builtin` half untouched. Building it here rather than in
* a component is what keeps the two frontends sending the same single request for the same set:
* a screen that looped over its rows would send one `PUT` per tool (and two writes for a reader
* who pressed twice).
*
* A server with no tools answers `null` — there is nothing to set, and a `{ mcp: {} }` write
* would be a request that changes nothing.
*
* @param tools the server's tools, as {@link mcpServerTools} joins them
* @param policy the permission to store for all of them
*/
function mcpServerPolicyBody(tools, policy) {
	if (tools.length === 0) return null;
	return { mcp: Object.fromEntries(tools.map((tool) => [tool.offeredName, policy])) };
}
/**
* The state of a server's tools as one value.
*
* @param tools the server's tools, as {@link mcpServerTools} joins them
*/
function mcpToolPolicyState(tools) {
	const first = tools[0];
	if (first === void 0) return null;
	return tools.every((tool) => tool.policy === first.policy) ? {
		kind: "uniform",
		policy: first.policy
	} : { kind: "mixed" };
}
/**
* The words for that state — "Ask (all)" for a permission every tool shares, "Mixed" when they
* differ — the same in both frontends.
*
* @param state the state {@link mcpToolPolicyState} answered
*/
function mcpToolPolicyStateLabel(state) {
	return state.kind === "mixed" ? "Mixed" : `${toolPolicyLabel(state.policy)} (all)`;
}
/** The permissions a per-tool picker offers, in the order it offers them. */
const TOOL_POLICIES = [
	"allow",
	"ask",
	"deny"
];
/** The words for a permission, the same in both frontends. */
const POLICY_LABELS = {
	allow: "Allow",
	ask: "Ask",
	deny: "Deny"
};
/**
* The words for a permission.
*
* @param policy `allow`, `ask` or `deny`
*/
function toolPolicyLabel(policy) {
	return POLICY_LABELS[policy];
}
/** The remote tool entries of a tool-settings list, grouped by the server they belong to. */
function mcpToolEntriesByServer(entries) {
	const groups = /* @__PURE__ */ new Map();
	for (const entry of entries) {
		if (entry.source !== "mcp" || entry.mcp_server === void 0) continue;
		const group = groups.get(entry.mcp_server);
		if (group === void 0) groups.set(entry.mcp_server, [entry]);
		else group.push(entry);
	}
	return groups;
}
/**
* The server a `mcp_server` name stands for, or `null` (epic #303, X10).
*
* A settings read names a server by its **name** and the resource carries both a name and an
* id; this is the one place the two are put together, so a screen does not grow its own
* `find`.
*
* @param servers the caller's servers
* @param name the name the tool settings carry
*/
function mcpServerByName(servers, name) {
	if (name === void 0) return null;
	return servers.find((server) => server.name === name) ?? null;
}
/**
* The server an id names, or `null`.
*
* @param servers the caller's servers
* @param id the `mcps_` id
*/
function mcpServerById(servers, id) {
	if (id === null || id === void 0) return null;
	return servers.find((server) => server.id === id) ?? null;
}
/** The marker the brain's MCP failure message opens with, and the name it quotes there. */
const MCP_FAILURE_PREFIX = "The MCP server ";
const MCP_FAILURE_SUFFIX = " could not be used:";
/**
* The MCP failure a transcript's `lastError` is, or `null` (epic #303, X10; #313).
*
* `null` for anything else — a step limit, a provider error, no error at all — so a screen
* shows this notice for exactly the two errors it is about. The server name is read from the
* message the brain writes (`The MCP server "<name>" could not be used: …`), which is the only
* place the log carries it: the error's `type` says a server failed, not which one.
*
* @param error the transcript's `lastError`
*/
function mcpFailureNotice(error) {
	if (error === null) return null;
	if (error.type !== "mcp_connection_failed_error" && error.type !== "mcp_authentication_failed_error") return null;
	return {
		server: mcpServerFromError(error.message),
		message: error.message,
		reconnectable: true
	};
}
/**
* The server name inside a `mcp_*` error message, or `null`.
*
* The brain quotes it (`The MCP server "notes" could not be used: …`), and that is the
* sentence's own shape rather than a wire field — so this is a **reading of the message**,
* guarded to the prefix rather than a search for the first quoted word anywhere in the text. A
* message that does not open that way answers `null`, and a screen falls back to showing the
* failure without a reconnect target rather than pointing at a server it guessed.
*
* @param message the `session.error` message
*/
function mcpServerFromError(message) {
	if (!message.startsWith(MCP_FAILURE_PREFIX)) return null;
	const rest = message.slice(15);
	const end = rest.indexOf(MCP_FAILURE_SUFFIX);
	if (end <= 0) return null;
	return unquote(rest.slice(0, end));
}
/** Strip the quotes the brain wraps a name in, when it did. */
function unquote(value) {
	if (value.length >= 2 && value.startsWith("\"") && value.endsWith("\"")) return value.slice(1, -1);
	return value.length > 0 ? value : null;
}
/** The query parameter the OAuth callback answers a web flow with (epic #303, X10; #311). */
const MCP_CONNECTED_PARAM = "mcp_connected";
/**
* The server id an OAuth callback named, or `null` (epic #303, X10; #311; #313).
*
* A web flow's callback redirects the browser to the app's settings screen with
* `?mcp_connected=<id>#/settings`, and the app is what confirms the connection and refreshes the
* server's status. This reads that parameter out of a URL's **search** half — a caller passes
* `location.search` or a whole URL — and keeps the `#` fragment out of the way, because the
* query lands before the hash.
*
* @param search a query string (`?mcp_connected=…`), a URL, or `''`
*/
function mcpConnectedServerId(search) {
	const beforeHash = (search.startsWith("?") ? search.slice(1) : search).split("#", 1)[0] ?? "";
	for (const part of beforeHash.split("&")) {
		const separator = part.indexOf("=");
		if (separator === -1) continue;
		if (decodeURIComponent(part.slice(0, separator)) === "mcp_connected") {
			const value = decodeURIComponent(part.slice(separator + 1));
			return value.length > 0 ? value : null;
		}
	}
	return null;
}
/** How often a CLI's connect flow reads the server's status back, in milliseconds. */
const MCP_CONNECT_POLL_INTERVAL_MS = 2e3;
/** How long a CLI waits for a browser sign-in to complete, in milliseconds (five minutes). */
const MCP_CONNECT_TIMEOUT_MS = 3e5;
/**
* Wait until a server's OAuth flow completes, reading its status back (epic #303, X10; #313).
*
* This is the CLI's half of `connect`: `oh` opens the authorization URL in the **system
* browser** — a browser that may never have signed in to openharness, which is why the callback
* authenticates by the `state` alone — and then has nothing to do but watch the server's status
* change, because the callback lands in that browser rather than in the terminal. A web flow
* needs none of this: its callback redirects to the app, which refreshes the server itself.
*
* It reads the server **before** waiting (a fast sign-in is not missed by a whole interval), and
* answers as soon as the status stops being the one the flow was started for: `connected` for a
* success, `failed` for an `error` (the token exchange was refused, say), `timeout` when the
* deadline passes, and `stopped` when the caller aborts. Nothing here throws for a status: a
* failure is an answer, and the caller renders it.
*
* @param options where to read the server, how often, and how long
*/
async function waitForMcpConnection(options) {
	const intervalMs = options.intervalMs ?? 2e3;
	const timeoutMs = options.timeoutMs ?? 3e5;
	const sleep$1 = options.sleep ?? sleep;
	const startedAt = Date.now();
	for (;;) {
		const server = await options.get({ signal: options.signal });
		if (server.status === "connected" || server.status === "error") return {
			outcome: server.status === "connected" ? "connected" : "failed",
			server
		};
		if (aborted(options.signal)) return {
			outcome: "stopped",
			server
		};
		if (Date.now() - startedAt >= timeoutMs) return {
			outcome: "timeout",
			server
		};
		await sleep$1(intervalMs, options.signal);
		if (aborted(options.signal)) return {
			outcome: "stopped",
			server
		};
	}
}
/**
* Whether a wait has been cancelled.
*
* A function rather than an inline `signal?.aborted` because the narrowing a first check leaves
* behind would make the second one a type error — the property is re-read here, which is also
* what the wait really does between two reads.
*/
function aborted(signal) {
	return signal?.aborted === true;
}
//#endregion
export { OUTPUT_RESERVE_RATIO as A, truncationNotice as B, mcpToolPolicyStateLabel as C, COMPACTING_LABEL as D, CHARS_PER_TOKEN as E, estimateTokens as F, AsyncQueue as G, DeviceLoginError as H, manualCompactionNotice as I, AuthenticationError as J, sleep as K, modelContextBudget as L, contextAfterSummary as M, contextMeter as N, DEFAULT_COMPACTION_THRESHOLD as O, contextTokenBudget as P, summaryDescription as R, mcpToolPolicyState as S, waitForMcpConnection as T, OPENHARNESS_CLI_CLIENT_ID as U, isEventList as V, createAuthResource as W, apiErrorFromResponse as X, ResponseValidationError as Y, errorTypeForStatus as Z, mcpStatusLabel as _, TOOL_POLICIES as a, mcpToolEntriesByServer as b, mcpAuthLabel as c, mcpServerById as d, mcpServerByName as f, mcpStatusDetail as g, mcpServerTools as h, MCP_CONNECT_TIMEOUT_MS as i, compactionThreshold as j, DEFAULT_CONTEXT_TOKEN_BUDGET as k, mcpConnectedServerId as l, mcpServerPolicyBody as m, MCP_CONNECTED_PARAM as n, estimatedTokensLabel as o, mcpServerFromError as p, ApiError as q, MCP_CONNECT_POLL_INTERVAL_MS as r, mcpAuthHint as s, MCP_AUTH_TYPES as t, mcpFailureNotice as u, mcpStatusTone as v, toolPolicyLabel as w, mcpToolPolicyBody as x, mcpTestOutcome as y, summaryModelFallback as z };

//# sourceMappingURL=mcp-OaEFFRME.js.map